const HERO_WRAPPER_SELECTOR = '.hero-wrapper';
const INITIALIZED_ATTRIBUTE = 'heroScrollAnimationInitialized';
const HERO_PADDING_INPUT_DISTANCE = 750;
const HERO_SMOOTH_DURATION = 0.8;
const LENIS_EASING = (progress) => Math.min(1, 1.001 - 2 ** (-10 * progress));

function findHeroWrappers(root) {
  const wrappers = Array.from(root.querySelectorAll?.(HERO_WRAPPER_SELECTOR) ?? []);

  if (root.matches?.(HERO_WRAPPER_SELECTOR)) wrappers.unshift(root);

  return wrappers;
}

function lockHomepageScrollUntilPaddingCompletes(wrapper, timeline) {
  const pageWrapper = wrapper.closest('.page-wrapper');
  const scrollContainer = window.matchMedia('(min-width: 990px)').matches
    ? pageWrapper
    : document.scrollingElement;

  if (!scrollContainer) return () => {};

  const containerTop = scrollContainer === document.scrollingElement ? 0 : scrollContainer.getBoundingClientRect().top;
  const heroStart = scrollContainer.scrollTop + wrapper.getBoundingClientRect().top - containerTop;
  let lastTouchY = null;
  let targetProgress = timeline.progress();

  const consumeScrollInput = (delta) => {
    if (!delta || scrollContainer.scrollTop > heroStart + 1) return false;

    if (delta > 0 && targetProgress >= 1) return timeline.progress() < 0.999;
    if (delta < 0 && targetProgress <= 0) return timeline.progress() > 0.001;

    targetProgress = Math.max(0, Math.min(1, targetProgress + delta / HERO_PADDING_INPUT_DISTANCE));
    window.gsap.to(timeline, {
      progress: targetProgress,
      duration: HERO_SMOOTH_DURATION,
      ease: LENIS_EASING,
      overwrite: true,
    });
    return true;
  };

  // Lenis calls these consumers before changing the page scroll position. This
  // lets the hero finish its intro without Lenis queuing scroll underneath it.
  const scrollInputConsumers =
    window.themeScrollInputConsumers || (window.themeScrollInputConsumers = new Set());
  scrollInputConsumers.add(consumeScrollInput);

  const onWheel = (event) => {
    if (event.ctrlKey || event.defaultPrevented) return;
    if (consumeScrollInput(event.deltaY)) event.preventDefault();
  };

  const onTouchStart = (event) => {
    lastTouchY = event.touches[0]?.clientY ?? null;
  };

  const onTouchMove = (event) => {
    const touchY = event.touches[0]?.clientY;
    if (lastTouchY == null || touchY == null) return;

    const delta = lastTouchY - touchY;
    lastTouchY = touchY;
    if (consumeScrollInput(delta)) event.preventDefault();
  };

  const onKeyDown = (event) => {
    const isInteractiveTarget =
      event.target instanceof Element &&
      Boolean(event.target.closest('input, textarea, select, button, [contenteditable="true"]'));

    if (event.defaultPrevented || isInteractiveTarget) {
      return;
    }

    const keyDeltas = {
      ArrowDown: 40,
      ArrowUp: -40,
      PageDown: window.innerHeight * 0.9,
      PageUp: window.innerHeight * -0.9,
      ' ': window.innerHeight * (event.shiftKey ? -0.9 : 0.9),
    };
    const delta = keyDeltas[event.key];

    if (delta && consumeScrollInput(delta)) event.preventDefault();
  };

  window.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('touchstart', onTouchStart, { passive: true });
  window.addEventListener('touchmove', onTouchMove, { passive: false });
  window.addEventListener('keydown', onKeyDown);

  return () => {
    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('keydown', onKeyDown);
    scrollInputConsumers.delete(consumeScrollInput);
    window.gsap.killTweensOf(timeline);
  };
}

export function initHeroScrollAnimations({
  root = document,
  gsap = window.gsap,
  ScrollTrigger = window.ScrollTrigger,
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches,
} = {}) {
  if (reducedMotion || !gsap || !ScrollTrigger) return;

  gsap.registerPlugin(ScrollTrigger);

  findHeroWrappers(root).forEach((wrapper) => {
    const hero = wrapper.firstElementChild;
    const header = document.querySelector('#header-component');
    const scroller = wrapper.closest('.page-wrapper') || window;
    const shouldLockScroll = Boolean(wrapper.closest('.template-index'));

    if (!hero?.classList.contains('hero') || wrapper.dataset[INITIALIZED_ATTRIBUTE]) return;

    const updateHeroState = (progress) => {
      gsap.set(hero, { borderRadius: progress > 0 ? 24 : 0 });
      header?.toggleAttribute('data-hero-scrolled', progress > 0);
    };
    const timeline = gsap.timeline({
      paused: shouldLockScroll,
      onUpdate: () => updateHeroState(timeline.progress()),
      scrollTrigger: shouldLockScroll
        ? undefined
        : {
            trigger: wrapper,
            scroller,
            start: 0,
            end: `+=${HERO_PADDING_INPUT_DISTANCE}`,
            scrub: true,
          },
    });

    timeline.to(
      wrapper,
      {
        paddingTop: 25,
        paddingRight: 30,
        paddingBottom: 25,
        paddingLeft: 30,
        ease: 'none',
      },
      0
    );

    wrapper.dataset[INITIALIZED_ATTRIBUTE] = 'true';
    wrapper.heroScrollTimeline = timeline;
    wrapper.heroScrollHeader = header;
    wrapper.heroScrollCleanup = shouldLockScroll ? lockHomepageScrollUntilPaddingCompletes(wrapper, timeline) : null;
  });
}

function destroyHeroScrollAnimation(wrapper) {
  const timeline = wrapper.heroScrollTimeline;

  timeline?.scrollTrigger?.kill();
  timeline?.kill();
  wrapper.heroScrollCleanup?.();
  wrapper.heroScrollHeader?.removeAttribute('data-hero-scrolled');
  delete wrapper.heroScrollTimeline;
  delete wrapper.heroScrollHeader;
  delete wrapper.heroScrollCleanup;
  delete wrapper.dataset[INITIALIZED_ATTRIBUTE];
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  initHeroScrollAnimations();

  document.addEventListener('shopify:section:load', (event) => {
    initHeroScrollAnimations({ root: event.target });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    findHeroWrappers(event.target).forEach(destroyHeroScrollAnimation);
  });
}
